/**
 * The first-sign-in tour of the dashboard.
 *
 * Starts by itself the first time a person reaches a real page — never the sign-in or "Continue
 * as" screens, which sit outside the shell — and again whenever they press "Take the tour" in
 * Help. One stop per section of the rail they can open, then search, the shop, Help and their
 * account. A section they cannot open is not in the rail, so it is not in the tour.
 *
 * Next is the primary action, Back an outline, "Skip tour" a text link. Below 768px the card is a
 * bottom sheet, and the rail's sections — hidden in the menu on a phone — become one stop on the
 * menu button.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Portal from '@mui/material/Portal';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { selectUser, tourSeen, useMarkTourSeenMutation, useTour, type TourStep } from '@pos/shared';

import { useAppDispatch, useAppSelector } from '@/app/hooks';

import { START_TOUR_EVENT } from '../lib/startTour.js';


const SECTION_STEPS: readonly TourStep[] = [
  {
    target: 'nav-Home',
    title: 'Home',
    body: 'Today at a glance: what the shop has sold, the cash being held, and anything waiting for you.',
  },
  {
    target: 'nav-Selling',
    title: 'Sales',
    body: 'Every sale, newest first. Open one for its receipt, the payment and who sold it. Filter by dates or by who sold it.',
  },
  {
    target: 'nav-Money',
    title: 'Money',
    body: 'Payments, M-Pesa money that arrived without a sale, checking the books add up, and who is holding cash from their shift.',
  },
  {
    target: 'nav-Inventory',
    title: 'Inventory',
    body: 'Stock, products and their prices, moving stock between shops, stock counts, and faulty stock.',
  },
  {
    target: 'nav-Purchasing',
    title: 'Purchasing',
    body: 'Your suppliers, the stock you buy from them, and claims when something arrives faulty.',
  },
  {
    target: 'nav-Oversight',
    title: 'Oversight',
    body: 'Things that need a decision — approvals, the review queue, disputes — and brokers, customers and reports.',
  },
  {
    target: 'nav-Setup',
    title: 'Setup',
    body: 'People and what their role lets them do, shops, imports, printers, and the audit trail of who did what.',
  },
  {
    target: 'nav-You',
    title: 'You',
    body: 'Your own sales, your profile and password, and Help.',
  },
];

const MENU_STEP: TourStep = {
  target: 'menu',
  title: 'Everything is in this menu',
  body: 'Sales, money, stock, purchasing, people and reports — each section you can open. On a laptop it stays open down the left side.',
};

const TOP_STEPS: readonly TourStep[] = [
  {
    target: 'search',
    title: 'Is it in stock?',
    body: 'Type a product name or SKU to see how many there are, at this shop and the others.',
  },
  {
    target: 'shop',
    title: 'Which shop',
    body: 'Everything you see is for this shop. If you look after more than one, switch here — the colours change with it.',
  },
  {
    target: 'help',
    title: 'Help',
    body: 'How to do anything here, in plain words. You can take this tour again from there.',
  },
  {
    target: 'account',
    title: 'You',
    body: 'Your profile, light or dark, and signing out.',
  },
];

const CARD_WIDTH = 340;
const GAP = 12;
const PAD = 6;

export function DashboardTour() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const [markSeen] = useMarkTourSeenMutation();
  const sheet = useMediaQuery('(max-width:767.98px)');
  const railVisible = useMediaQuery(useTheme().breakpoints.up('md'));

  // Starts once per session for someone who has not seen it; `false`, not absent, because an
  // older server that does not send the flag must not show it to everyone.
  const [open, setOpen] = useState(false);
  const autoStarted = useRef(false);

  useEffect(() => {
    if (autoStarted.current || user?.dashboardTourSeen !== false) return;
    // A beat after the first page draws, so the rail and header are there to point at. Marked as
    // started when it fires, not before: in development React runs this effect twice, and a
    // marker set on the first run would stop the second from ever starting it.
    const timer = window.setTimeout(() => {
      autoStarted.current = true;
      setOpen(true);
    }, 600);
    return () => {
      window.clearTimeout(timer);
    };
  }, [user?.dashboardTourSeen]);

  useEffect(() => {
    const start = () => {
      setOpen(true);
    };
    window.addEventListener(START_TOUR_EVENT, start);
    return () => {
      window.removeEventListener(START_TOUR_EVENT, start);
    };
  }, []);

  const steps = useMemo(
    () => [...(railVisible ? SECTION_STEPS : [MENU_STEP]), ...TOP_STEPS],
    [railVisible],
  );

  const returnFocus = useRef<HTMLElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    if (user?.dashboardTourSeen === false) {
      dispatch(tourSeen('dashboard'));
      void markSeen('dashboard');
    }
    returnFocus.current?.focus();
  }, [dispatch, markSeen, user?.dashboardTourSeen]);

  const tour = useTour({ steps, open, onClose: close });

  useEffect(() => {
    if (open) returnFocus.current = document.activeElement as HTMLElement | null;
  }, [open]);

  if (!tour.open || tour.step === null) return null;

  return <TourCard tour={tour} sheet={sheet} />;
}

function TourCard({ tour, sheet }: { tour: ReturnType<typeof useTour>; sheet: boolean }) {
  const card = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(180);
  const step = tour.step!;
  const rect = tour.rect;
  const last = tour.index === tour.count - 1;
  const located = rect !== null;

  useLayoutEffect(() => {
    if (card.current) setHeight(card.current.offsetHeight);
    card.current?.focus();
  }, [tour.index, located]);

  const place = (): { top: number; left: number } => {
    if (rect === null) return { top: window.innerHeight / 2 - height / 2, left: window.innerWidth / 2 - CARD_WIDTH / 2 };
    const clampLeft = (left: number) => Math.min(Math.max(GAP, left), window.innerWidth - CARD_WIDTH - GAP);
    const clampTop = (top: number) => Math.min(Math.max(GAP, top), window.innerHeight - height - GAP);

    // A narrow target on the left — a section of the rail — gets the card beside it.
    if (rect.left + rect.width + GAP + CARD_WIDTH < window.innerWidth && rect.width < 300 && rect.left < 300) {
      return { top: clampTop(rect.top), left: rect.left + rect.width + GAP + PAD };
    }
    const below = rect.top + rect.height + GAP + PAD;
    if (below + height < window.innerHeight) return { top: below, left: clampLeft(rect.left) };
    return { top: clampTop(rect.top - GAP - PAD - height), left: clampLeft(rect.left) };
  };

  const position = place();
  const titleId = `tour-title-${tour.index}`;

  return (
    <Portal>
      {/* Clicks outside the card do nothing: the page underneath is for looking at, not using,
          until the tour is closed. */}
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400 }} aria-hidden />

      {rect !== null && (
        <Box
          aria-hidden
          sx={{
            position: 'fixed',
            zIndex: 1401,
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 'var(--pos-radius-md)',
            boxShadow: '0 0 0 9999px var(--pos-scrim)',
            outline: '2px solid var(--pos-action-base)',
            pointerEvents: 'none',
            transition: 'top var(--pos-duration-base), left var(--pos-duration-base), width var(--pos-duration-base), height var(--pos-duration-base)',
          }}
        />
      )}

      <Box
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        sx={{
          position: 'fixed',
          zIndex: 1402,
          bgcolor: 'var(--pos-surface-raised)',
          color: 'var(--pos-text-primary)',
          boxShadow: 'var(--pos-shadow-xl)',
          outline: 'none',
          p: 2.5,
          ...(sheet
            ? {
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 'var(--pos-radius-sheet) var(--pos-radius-sheet) 0 0',
                pb: 'max(20px, env(safe-area-inset-bottom))',
              }
            : {
                top: position.top,
                left: position.left,
                width: CARD_WIDTH,
                borderRadius: 'var(--pos-radius-card)',
              }),
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {tour.index + 1} of {tour.count}
        </Typography>
        <Typography id={titleId} sx={{ fontWeight: 700, fontSize: 16, mt: 0.5 }}>
          {step.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {step.body}
        </Typography>

        <Stack
          direction={sheet ? 'column-reverse' : 'row'}
          spacing={1}
          alignItems={sheet ? 'stretch' : 'center'}
          sx={{ mt: 2 }}
        >
          <Button variant="text" onClick={tour.close} sx={{ mr: sheet ? 0 : 'auto', color: 'text.secondary' }}>
            Skip tour
          </Button>
          {tour.index > 0 && (
            <Button variant="outlined" onClick={tour.previous} fullWidth={sheet}>
              Back
            </Button>
          )}
          <Button variant="contained" onClick={tour.next} fullWidth={sheet}>
            {last ? 'Done' : 'Next'}
          </Button>
        </Stack>
      </Box>
    </Portal>
  );
}
